import type { DomainEventType } from "@repo/embed";
import { z } from "zod";

/**
 * Event types a retailer's endpoint can subscribe to, with the words the admin sees. One list
 * for the Integrations screen and the API. Partner-only types (`organization.ready`) are for
 * the partner's own endpoints, and `webhook.test` reaches an endpoint only through "Send test
 * event", whatever it subscribes to, so neither is offered.
 */
export const WEBHOOK_EVENTS = [
	{
		type: "cart.item_added",
		group: "Basket",
		label: "Item added",
	},
	{
		type: "cart.item_updated",
		group: "Basket",
		label: "Quantity changed",
	},
	{
		type: "cart.item_removed",
		group: "Basket",
		label: "Item removed",
	},
	{
		type: "cart.cleared",
		group: "Basket",
		label: "Basket emptied",
	},
	{
		type: "cart.checkout_requested",
		group: "Basket",
		label: "Checkout requested",
	},
	{
		type: "conversation.handoff_requested",
		group: "Conversations",
		label: "Shopper asked for a person",
	},
	{
		type: "conversation.handoff_released",
		group: "Conversations",
		label: "Handed back to the agent",
	},
	{
		type: "conversation.message",
		group: "Conversations",
		label: "Staff replied",
	},
	{
		type: "order.attributed",
		group: "Orders",
		label: "Order linked to a conversation",
	},
	{
		type: "order.placed",
		group: "Orders",
		label: "Order placed through Hephon",
	},
	{
		type: "order.status_changed",
		group: "Orders",
		label: "Order status changed",
	},
	{
		type: "order.updated",
		group: "Orders",
		label: "Order lines or details changed",
	},
	{
		type: "order.issue_reported",
		group: "Orders",
		label: "Shopper reported a problem with an order",
	},
	{
		type: "shopper.data_deleted",
		group: "Privacy",
		label: "Shopper data deleted",
	},
] as const satisfies readonly {
	type: DomainEventType;
	group: string;
	label: string;
}[];

export type WebhookEventType = (typeof WEBHOOK_EVENTS)[number]["type"];

export const WEBHOOK_EVENT_TYPES = WEBHOOK_EVENTS.map((e) => e.type) as [
	WebhookEventType,
	...WebhookEventType[],
];

/** Endpoints per organization. Stripe allows 16; one per system is the norm. */
export const MAX_WEBHOOK_ENDPOINTS = 20;

/** How long the replaced secret still signs deliveries after a rotation. */
export const SECRET_OVERLAP_HOURS = 24;

export const endpointUrlSchema = z
	.string()
	.trim()
	.min(1, "Enter the URL that receives events")
	.max(2048, "Keep the URL under 2048 characters");

export const endpointDescriptionSchema = z
	.string()
	.trim()
	.max(200, "Keep the description under 200 characters");

/** Empty = every event type, including ones added later. */
export const eventTypesSchema = z
	.array(z.enum(WEBHOOK_EVENT_TYPES))
	.max(WEBHOOK_EVENT_TYPES.length);

/** The delivery log's status filter. `retrying` also covers deliveries not sent yet. */
export const DELIVERY_FILTERS = [
	"succeeded",
	"retrying",
	"dead",
	"cancelled",
] as const;
export type DeliveryFilter = (typeof DELIVERY_FILTERS)[number];

/** One entry of `webhook_delivery.attempts`, as the worker writes it (newest last). */
export interface DeliveryAttempt {
	at: string;
	durationMs: number;
	statusCode?: number;
	error?: string;
	/** First 2 KB of the receiver's answer. */
	responseBody?: string;
}
